import { LitElement, html, css, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { router } from '../../utils/router.js'
import { authService } from '../../services/auth/auth-service.js'
import { teamStorage, teamMemberStorage } from '../../storage/team-storage.js'
import { sendTeamInvite } from '../../services/teams/teams-api.js'
import type { Team, TeamEmergencyContact } from '../../schemas/team.js'
import { resizeImageToDataUrl } from '../../utils/image-resize.js'
import type { User } from '../../schemas/user.js'
import type { TeamMemberWithUser } from '../../storage/team-storage.js'
import { db } from '../../database/schema.js'
import { syncChildrenOnNavigate } from '../../storage/sync-manager.js'
import { showToast } from '../common/cairn-toast.js'
import { blockStore } from '../../services/moderation/block-store.js'

import '@material/web/iconbutton/icon-button.js'
import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/textfield/outlined-text-field.js'
import '@material/web/progress/linear-progress.js'
import '../common/confirm-dialog.js'
import type { ConfirmDialog } from '../common/confirm-dialog.js'
void import('../ui/rich-text-renderer.js')

@customElement('cairn-team-detail')
export class CairnTeamDetail extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      width: 100%;
      background: var(--cairn-background-color, #f8fafc);
      overflow: hidden;
    }

    .header {
      background: var(--cairn-surface-color, #ffffff);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
      padding: var(--cairn-spacing-md, 16px);
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      flex: 0 0 auto;
    }

    .back-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
    }

    .back-button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .header-title {
      font-size: var(--cairn-text-size-xl, 24px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    @media (min-width: 769px) {
      .header {
        display: none;
      }
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .content-scroll {
      flex: 1;
      overflow: auto;
      transition: transform 0.15s ease-out;
    }

    .content-wrapper {
      flex: 1;
      padding: var(--cairn-spacing-lg, 24px);
      display: flex;
      justify-content: center;
    }

    .content {
      width: 100%;
      max-width: 800px;
    }

    .team-header {
      display: flex;
      align-items: flex-start;
      gap: var(--cairn-spacing-sm, 8px);
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .team-name {
      font-size: 28px;
      font-weight: 700;
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
      flex: 1;
    }

    .team-actions {
      display: flex;
      gap: var(--cairn-spacing-xs, 4px);
      flex-shrink: 0;
    }

    .team-actions .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 24px;
    }

    .section {
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
      padding-bottom: var(--cairn-spacing-xs, 4px);
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .section-title {
      font-size: var(--cairn-text-size-md, 16px);
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .description-text {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
      line-height: 1.5;
    }

    .description-empty {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
      font-style: italic;
    }

    .member-list {
      display: flex;
      flex-direction: column;
    }

    .member-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 0;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .member-item:last-child {
      border-bottom: none;
    }

    .member-avatar {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .member-avatar-placeholder {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
      flex-shrink: 0;
    }

    .member-info {
      flex: 1;
      min-width: 0;
    }

    .member-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .member-you {
      color: var(--cairn-text-secondary-color, #64748b);
      font-weight: 400;
    }

    .role-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: var(--cairn-text-size-xs, 12px);
      font-weight: 500;
      text-transform: uppercase;
      flex-shrink: 0;
    }

    .role-badge.owner {
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    .role-badge.member {
      background: rgba(100, 116, 139, 0.1);
      color: #64748b;
    }

    .member-actions {
      display: flex;
      gap: 4px;
      flex-shrink: 0;
      margin-left: 4px;
    }

    .member-actions md-icon-button {
      --md-icon-button-icon-size: 20px;
      --md-icon-button-state-layer-height: 32px;
      --md-icon-button-state-layer-width: 32px;
    }

    .member-actions .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .invite-form {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: 12px;
      margin-bottom: 8px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .invite-form-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .invite-form-row md-outlined-text-field {
      flex: 1;
    }

    .invite-form-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    .invite-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .invite-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px 4px 12px;
      border-radius: 16px;
      font-size: var(--cairn-text-size-sm, 14px);
      background: var(--cairn-primary-bg-light-color, rgba(59, 130, 246, 0.15));
      color: var(--cairn-text-color, #1e293b);
    }

    .invite-chip button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border: none;
      background: none;
      border-radius: 50%;
      cursor: pointer;
      padding: 0;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 16px;
      font-family: 'Material Symbols Outlined';
    }

    .invite-chip button:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-color, #1e293b);
    }

    .suggestions {
      margin-top: 4px;
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      max-height: 200px;
      overflow-y: auto;
    }

    .suggestion-item {
      display: flex;
      flex-direction: column;
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: 1px solid var(--cairn-border-color, #e2e8f0);
    }

    .suggestion-item:last-child {
      border-bottom: none;
    }

    .suggestion-item:hover {
      background: var(--cairn-primary-bg-light-color, rgba(59, 130, 246, 0.04));
    }

    .suggestion-name {
      font-weight: 500;
      color: var(--cairn-text-color, #1e293b);
      font-size: var(--cairn-text-size-sm, 14px);
    }

    .suggestion-email {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-xs, 12px);
    }

    .team-logo-area {
      display: flex;
      align-items: center;
      gap: var(--cairn-spacing-md, 16px);
      margin-bottom: var(--cairn-spacing-lg, 24px);
    }

    .team-logo {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .team-logo-placeholder {
      width: 80px;
      height: 80px;
      border-radius: 12px;
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      font-weight: 700;
      flex-shrink: 0;
    }

    .logo-upload-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 8px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-sm, 14px);
      cursor: pointer;
    }

    .logo-upload-label:hover {
      background: var(--cairn-surface-hover-color, #f1f5f9);
    }

    .logo-upload-label .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
    }

    .logo-upload-input {
      display: none;
    }

    .logo-remove-btn {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 6px 12px;
      border-radius: 8px;
      border: none;
      background: none;
      color: var(--cairn-error-color, #ef4444);
      font-size: var(--cairn-text-size-sm, 14px);
      cursor: pointer;
    }

    .emergency-contact-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-color, #1e293b);
    }

    .emergency-contact-info .phone-link {
      color: var(--cairn-primary-color, #3b82f6);
      text-decoration: none;
    }

    .emergency-contact-meta {
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: var(--cairn-text-size-xs, 12px);
    }

    .emergency-contact-fields {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .edit-actions {
      display: flex;
      gap: 8px;
      margin-top: var(--cairn-spacing-sm, 8px);
    }
  `

  @property({ type: String }) teamId = ''

  @state() private team: Team | null = null
  @state() private members: TeamMemberWithUser[] = []
  @state() private currentUserRole: string | null = null
  @state() private syncing = false
  @state() private showInviteForm = false
  @state() private inviteEmails: string[] = []
  @state() private inviteInput = ''
  @state() private inviting = false
  @state() private knownUsers: User[] = []
  @state() private showSuggestions = false
  @state() private editing = false
  @state() private editLogo: string | undefined = undefined
  @state() private editTeamEmergencyContact: TeamEmergencyContact | undefined = undefined

  private get isOwner(): boolean {
    return this.currentUserRole === 'owner'
  }

  private get currentUserId(): string | undefined {
    return authService.getState().userId
  }

  override connectedCallback(): void {
    super.connectedCallback()
    void this.loadData()
    void this.loadKnownUsers()
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('teamId') && this.teamId) {
      void this.loadData()
    }
  }

  private async loadKnownUsers(): Promise<void> {
    const currentId = this.currentUserId
    const allUsers = await db.users.toArray()
    this.knownUsers = allUsers.filter(u => u.id !== currentId)
  }

  private async loadData(): Promise<void> {
    if (!this.teamId) return

    await this.loadLocalData()

    this.syncing = true
    try {
      await syncChildrenOnNavigate('team', this.teamId)
      await this.loadLocalData()
    } catch {
      // sync errors shown via toast
    }
    this.syncing = false
  }

  private async loadLocalData(): Promise<void> {
    const teamResult = await teamStorage.getById(this.teamId)
    this.team = teamResult.data ?? null

    const membersResult = await teamMemberStorage.getTeamMembersWithUsers(this.teamId)
    this.members = membersResult.data ?? []

    const userId = this.currentUserId
    if (userId) {
      const membership = this.members.find(m => m.userId === userId)
      this.currentUserRole = membership?.role ?? null
    }
  }

  // --- Pull to refresh ---

  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    this.syncing = true
    try {
      await syncChildrenOnNavigate('team', this.teamId)
      await this.loadLocalData()
    } finally {
      this.syncing = false
    }
  }

  // --- Invite flow ---

  private addInviteEmail(email: string): void {
    const trimmed = email.toLowerCase().trim()
    if (trimmed && trimmed.includes('@') && !this.inviteEmails.includes(trimmed)) {
      this.inviteEmails = [...this.inviteEmails, trimmed]
    }
    this.inviteInput = ''
    this.showSuggestions = false
  }

  private removeInviteEmail(email: string): void {
    this.inviteEmails = this.inviteEmails.filter(e => e !== email)
  }

  private handleInviteKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (this.inviteInput.trim()) {
        this.addInviteEmail(this.inviteInput)
      }
    }
    if (e.key === 'Backspace' && !this.inviteInput && this.inviteEmails.length > 0) {
      this.inviteEmails = this.inviteEmails.slice(0, -1)
    }
  }

  private handleInviteInput(e: InputEvent): void {
    const val = (e.target as HTMLInputElement).value
    if (val.includes(',')) {
      const parts = val.split(',')
      for (const part of parts.slice(0, -1)) {
        if (part.trim()) this.addInviteEmail(part)
      }
      this.inviteInput = parts[parts.length - 1]
    } else {
      this.inviteInput = val
    }
    this.showSuggestions = this.inviteInput.length > 0 && this.filteredSuggestions.length > 0
  }

  private get filteredSuggestions(): User[] {
    if (!this.inviteInput.trim()) return []
    const q = this.inviteInput.toLowerCase()
    const memberIds = new Set(this.members.map(m => m.userId))
    return this.knownUsers
      .filter(u => {
        if (memberIds.has(u.id)) return false
        if (u.email && this.inviteEmails.includes(u.email.toLowerCase())) return false
        return u.displayName.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      })
      .slice(0, 5)
  }

  private async handleSendInvites(): Promise<void> {
    if (this.inviteEmails.length === 0) return
    this.inviting = true
    let sent = 0
    let lastError = ''
    for (const email of this.inviteEmails) {
      try {
        const result = await sendTeamInvite(this.teamId, email)
        if (result.success) {
          sent++
        } else {
          lastError = result.error ?? 'Invite failed'
        }
      } catch (e) {
        lastError = e instanceof Error ? e.message : 'Network error'
      }
    }
    this.inviting = false
    this.showInviteForm = false
    this.inviteEmails = []
    this.inviteInput = ''
    if (sent > 0) {
      showToast('success', `${sent} invite${sent > 1 ? 's' : ''} sent`)
    } else if (lastError) {
      showToast('error', lastError)
    }
  }

  // --- Actions ---

  private async handleDelete(): Promise<void> {
    if (!this.team) return
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Team'
      dialog.message = `Delete "${this.team.name}"? This cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    await teamStorage.softDelete(this.team.id)
    showToast('success', 'Team deleted')
    router.navigate('/teams')
  }

  private get isSoleOwner(): boolean {
    const owners = this.members.filter(m => m.role === 'owner')
    return this.isOwner && owners.length === 1
  }

  private async handleLeave(): Promise<void> {
    const userId = this.currentUserId
    if (!userId) return

    if (this.isSoleOwner) {
      showToast('error', 'You must assign another owner before leaving')
      return
    }

    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Leave Team'
      dialog.message = `Leave "${this.team?.name}"? You'll need a new invite to rejoin.`
      dialog.confirmLabel = 'Leave'
      const confirmed = await dialog.show()
      dialog.confirmLabel = 'Delete'
      if (!confirmed) return
    }

    await teamMemberStorage.removeMember(this.teamId, userId)
    showToast('success', 'Left team')
    router.navigate('/teams')
  }

  private handleEdit(): void {
    this.editing = true
    this.editLogo = this.team?.logo
    this.editTeamEmergencyContact = this.team?.emergencyContact
      ? { ...this.team.emergencyContact }
      : undefined
  }

  private async handleSaveEdit(): Promise<void> {
    if (!this.team) return
    const updates: Record<string, unknown> = {}
    if (this.editLogo !== this.team.logo) {
      updates.logo = this.editLogo
    }
    const ecChanged =
      JSON.stringify(this.editTeamEmergencyContact) !== JSON.stringify(this.team.emergencyContact)
    if (ecChanged) {
      updates.emergencyContact = this.editTeamEmergencyContact
    }
    if (Object.keys(updates).length > 0) {
      await teamStorage.updateTeam(this.team.id, updates as Partial<Team>)
      await this.loadLocalData()
    }
    this.editing = false
    showToast('success', 'Team updated')
  }

  private handleCancelEdit(): void {
    this.editing = false
    this.editLogo = undefined
    this.editTeamEmergencyContact = undefined
  }

  private async handleLogoUpload(e: Event): Promise<void> {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file?.type.startsWith('image/')) return
    try {
      this.editLogo = await resizeImageToDataUrl(file)
    } catch {
      showToast('error', 'Image too large')
    }
  }

  // --- Render ---

  override render(): unknown {
    if (!this.team) {
      return html`
        <div class="header">
          <button
            class="back-button"
            @click=${() => router.navigate('/teams')}
            aria-label="Go back"
          >
            <span class="material-icons" style="font-family: 'Material Symbols Outlined'"
              >arrow_back</span
            >
          </button>
          <h1 class="header-title">Team</h1>
        </div>
        <div class="content-scroll">
          <div style="text-align: center; padding: 32px; color: var(--cairn-text-secondary-color);">
            Loading...
          </div>
        </div>
      `
    }

    return html`
      <div class="header">
        <button class="back-button" @click=${() => router.navigate('/teams')} aria-label="Go back">
          <span class="material-icons" style="font-family: 'Material Symbols Outlined'"
            >arrow_back</span
          >
        </button>
        <h1 class="header-title">${this.team.name}</h1>
      </div>

      ${this.syncing
        ? html`<md-linear-progress class="sync-bar" indeterminate></md-linear-progress>`
        : nothing}

      <div
        class="content-scroll"
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleTouchMove}
        @touchend=${this.handleTouchEnd}
      >
        <div class="content-wrapper">
          <div class="content">
            <div class="team-header">
              <h1 class="team-name">${this.team.name}</h1>
              <div class="team-actions">
                ${this.editing
                  ? html`
                      <md-text-button @click=${() => this.handleCancelEdit()}
                        >Cancel</md-text-button
                      >
                      <md-filled-button @click=${() => this.handleSaveEdit()}
                        >Save</md-filled-button
                      >
                    `
                  : html`
                      ${this.isOwner
                        ? html`
                            <md-icon-button
                              @click=${() => this.handleEdit()}
                              aria-label="Edit team"
                            >
                              <span class="material-icons">edit</span>
                            </md-icon-button>
                            <md-icon-button
                              @click=${() => this.handleDelete()}
                              aria-label="Delete team"
                            >
                              <span class="material-icons">delete</span>
                            </md-icon-button>
                          `
                        : nothing}
                      <md-icon-button @click=${() => this.handleLeave()} aria-label="Leave team">
                        <span class="material-icons">logout</span>
                      </md-icon-button>
                    `}
              </div>
            </div>

            ${this.renderLogo()} ${this.renderDescription()} ${this.renderTeamEmergencyContact()}
            ${this.renderMembers()}
          </div>
        </div>
      </div>

      <confirm-dialog></confirm-dialog>
    `
  }

  private renderLogo(): unknown {
    const logo = this.editing ? this.editLogo : this.team?.logo
    const initial = this.team?.name?.charAt(0).toUpperCase() ?? '?'

    return html`
      <div class="team-logo-area">
        ${logo
          ? html`<img class="team-logo" src="${logo}" alt="Team logo" />`
          : html`<div class="team-logo-placeholder">${initial}</div>`}
        ${this.editing
          ? html`
              <div>
                <label class="logo-upload-label">
                  <span class="material-icons">upload</span>
                  ${logo ? 'Change' : 'Upload'} Logo
                  <input
                    class="logo-upload-input"
                    type="file"
                    accept="image/*"
                    @change=${(e: Event) => this.handleLogoUpload(e)}
                  />
                </label>
                ${logo
                  ? html`<button
                      class="logo-remove-btn"
                      @click=${() => {
                        this.editLogo = undefined
                      }}
                    >
                      Remove
                    </button>`
                  : nothing}
              </div>
            `
          : nothing}
      </div>
    `
  }

  private renderTeamEmergencyContact(): unknown {
    if (this.editing) {
      const ec = this.editTeamEmergencyContact
      return html`
        <div class="section">
          <div class="section-header">
            <h2 class="section-title">Emergency Contact</h2>
          </div>
          <div class="emergency-contact-fields">
            <md-outlined-text-field
              label="Name"
              .value=${ec?.name ?? ''}
              @input=${(e: InputEvent) => {
                const val = (e.target as HTMLInputElement).value
                this.editTeamEmergencyContact = {
                  name: val,
                  phone: ec?.phone ?? '',
                }
              }}
              style="width: 100%;"
            ></md-outlined-text-field>
            <md-outlined-text-field
              label="Phone"
              type="tel"
              .value=${ec?.phone ?? ''}
              @input=${(e: InputEvent) => {
                const val = (e.target as HTMLInputElement).value
                this.editTeamEmergencyContact = {
                  name: ec?.name ?? '',
                  phone: val,
                }
              }}
              style="width: 100%;"
            ></md-outlined-text-field>
          </div>
        </div>
      `
    }

    const ec = this.team?.emergencyContact
    if (!ec) return nothing

    return html`
      <div class="section">
        <div class="section-header">
          <h2 class="section-title">Emergency Contact</h2>
        </div>
        <div class="emergency-contact-info">
          <div>${ec.name}</div>
          <a class="phone-link" href="tel:${ec.phone}">${ec.phone}</a>
        </div>
      </div>
    `
  }

  private renderDescription(): unknown {
    return html`
      <div class="section">
        <div class="section-header">
          <h2 class="section-title">Description</h2>
        </div>
        ${this.team?.descriptionRich
          ? html`<rich-text-renderer .content=${this.team.descriptionRich}></rich-text-renderer>`
          : this.team?.description
            ? html`<p class="description-text">${this.team.description}</p>`
            : html`<p class="description-empty">No description</p>`}
      </div>
    `
  }

  private renderMembers(): unknown {
    const suggestions = this.filteredSuggestions

    return html`
      <div class="section">
        <div class="section-header">
          <h2 class="section-title">Members (${this.members.length})</h2>
          <md-icon-button
            @click=${() => {
              this.showInviteForm = !this.showInviteForm
            }}
            aria-label="Invite members"
          >
            <span class="material-icons" style="font-family: 'Material Symbols Outlined'"
              >${this.showInviteForm ? 'close' : 'person_add'}</span
            >
          </md-icon-button>
        </div>

        ${this.showInviteForm
          ? html`
              <div class="invite-form">
                ${this.inviteEmails.length > 0
                  ? html`
                      <div class="invite-chips">
                        ${this.inviteEmails.map(
                          email => html`
                            <span class="invite-chip">
                              ${email}
                              <button @click=${() => this.removeInviteEmail(email)}>close</button>
                            </span>
                          `
                        )}
                      </div>
                    `
                  : nothing}

                <md-outlined-text-field
                  label="Email address"
                  type="email"
                  .value=${this.inviteInput}
                  @input=${(e: InputEvent) => this.handleInviteInput(e)}
                  @keydown=${(e: KeyboardEvent) => this.handleInviteKeydown(e)}
                  @blur=${() =>
                    setTimeout(() => {
                      this.showSuggestions = false
                    }, 200)}
                  @focus=${() => {
                    if (this.inviteInput && suggestions.length > 0) this.showSuggestions = true
                  }}
                  style="width: 100%;"
                ></md-outlined-text-field>

                ${this.showSuggestions && suggestions.length > 0
                  ? html`
                      <div class="suggestions">
                        ${suggestions.map(
                          user => html`
                            <div
                              class="suggestion-item"
                              @mousedown=${(e: Event) => {
                                e.preventDefault()
                                this.addInviteEmail(user.email)
                              }}
                            >
                              <span class="suggestion-name">${user.displayName}</span>
                              <span class="suggestion-email">${user.email}</span>
                            </div>
                          `
                        )}
                      </div>
                    `
                  : nothing}

                <div class="invite-form-actions">
                  <md-text-button
                    @click=${() => {
                      this.showInviteForm = false
                      this.inviteEmails = []
                      this.inviteInput = ''
                    }}
                    >Cancel</md-text-button
                  >
                  <md-filled-button
                    @click=${() => this.handleSendInvites()}
                    ?disabled=${this.inviteEmails.length === 0 || this.inviting}
                    >${this.inviting ? 'Sending...' : 'Send Invites'}</md-filled-button
                  >
                </div>
              </div>
            `
          : nothing}

        <div class="member-list">
          ${this.members
            .filter(m => m.userId === this.currentUserId || !blockStore.isBlocked(m.userId))
            .map(member => this.renderMember(member))}
        </div>
      </div>
    `
  }

  private renderMember(member: TeamMemberWithUser): unknown {
    const isCurrentUser = member.userId === this.currentUserId
    const auth = authService.getState()
    const displayName =
      member.user?.displayName ??
      (isCurrentUser ? auth.displayName : undefined) ??
      member.userId.slice(0, 8)
    const picture =
      (member.user as User | undefined)?.picture ?? (isCurrentUser ? auth.picture : undefined)
    const isBlocked = blockStore.isBlocked(member.userId)

    return html`
      <div class="member-item">
        ${picture
          ? html`<img class="member-avatar" src="${picture}" alt="" />`
          : html`<div class="member-avatar-placeholder">
              ${displayName.charAt(0).toUpperCase()}
            </div>`}
        <div class="member-info">
          <div class="member-name">
            ${displayName} ${isCurrentUser ? html` <span class="member-you">(you)</span>` : nothing}
          </div>
        </div>
        <span class="role-badge ${member.role}">${member.role}</span>
        ${!isCurrentUser
          ? html`
              <div class="member-actions">
                <md-icon-button
                  title="Report ${displayName}"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    void this.handleReportUser(member.userId, displayName)
                  }}
                  aria-label="Report user"
                >
                  <span class="material-icons">flag</span>
                </md-icon-button>
                <md-icon-button
                  title="${isBlocked ? `Unblock ${displayName}` : `Block ${displayName}`}"
                  @click=${(e: Event) => {
                    e.stopPropagation()
                    void this.handleBlockUser(member.userId, displayName)
                  }}
                  aria-label="${isBlocked ? 'Unblock user' : 'Block user'}"
                >
                  <span class="material-icons">${isBlocked ? 'person_add' : 'block'}</span>
                </md-icon-button>
              </div>
            `
          : nothing}
      </div>
    `
  }

  private async handleReportUser(userId: string, displayName: string): Promise<void> {
    await import('../common/report-dialog.js')
    let dialog = document.body.querySelector('report-dialog')
    if (!dialog) {
      dialog = document.createElement('report-dialog')
      document.body.appendChild(dialog)
    }
    await dialog.show('user', userId, displayName)
  }

  private async handleBlockUser(userId: string, displayName: string): Promise<void> {
    if (blockStore.isBlocked(userId)) {
      const success = await blockStore.unblock(userId)
      if (success) {
        showToast('success', `${displayName} unblocked`)
        this.requestUpdate()
      } else {
        showToast('error', 'Failed to unblock user')
      }
      return
    }

    await import('../common/block-dialog.js')
    let dialog = document.body.querySelector('block-dialog')
    if (!dialog) {
      dialog = document.createElement('block-dialog')
      document.body.appendChild(dialog)
    }

    const action = await dialog.show(userId, displayName)

    if (action) {
      for (const teamId of action.teamsToRemoveFrom) {
        try {
          await teamMemberStorage.removeMember(teamId, userId)
        } catch {
          /* best effort */
        }
      }
      for (const teamId of action.teamsToLeave) {
        const currentUserId = this.currentUserId
        if (currentUserId) {
          try {
            await teamMemberStorage.removeMember(teamId, currentUserId)
          } catch {
            /* best effort */
          }
        }
      }
      const { planStorage } = await import('../../storage/plan-storage.js')
      for (const planId of action.plansToRemoveFrom) {
        try {
          await planStorage.removeMember(planId, userId)
        } catch {
          /* best effort */
        }
      }
      for (const planId of action.plansToLeave) {
        const currentUserId = this.currentUserId
        if (currentUserId) {
          try {
            await planStorage.removeMember(planId, currentUserId)
          } catch {
            /* best effort */
          }
        }
      }
      await this.loadLocalData()
      this.requestUpdate()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cairn-team-detail': CairnTeamDetail
  }
}
